/* El tablero de herramientas, sin navegador ni base.
 *
 * Acá vive lo que se puede probar sin montar nada: los estados, la cuenta de
 * arriba, el buscador y los filtros. La pantalla (`routes/Herramientas.jsx`)
 * sólo dibuja; el servicio (`services/herramientas.js`) sólo habla con
 * Supabase. Es la misma separación que `shared/crt` ↔ `services/crt`, y es la
 * que permite fijar en un test que buscar «Carlos Ruiz» encuentra la amoladora
 * sin renderizar un tablero entero.
 *
 * ── EL ESTADO NO SE CALCULA ACÁ ──────────────────────────────────────────────
 *
 * Viene resuelto de la vista `taller_tablero`. Si se recalculara en el
 * navegador habría dos definiciones de DEMORADA —una acá y otra en la base,
 * que es la que va a leer el bot— y el día que difieran, el tablero y el
 * WhatsApp dicen cosas distintas de la misma herramienta. Acá sólo se lo
 * nombra y se lo cuenta.
 */

import { siglaDe } from "./empresa";

export const ESTADOS = {
  DISPONIBLE: { rotulo: "Disponible", clase: "hrr--ok", punto: "🟢" },
  PRESTADA: { rotulo: "En uso", clase: "hrr--uso", punto: "🔴" },
  DEMORADA: { rotulo: "Demorada", clase: "hrr--tarde", punto: "🟠" },
  MANTENIMIENTO: { rotulo: "Fuera de servicio", clase: "hrr--fs", punto: "⚫" },
};

/* Un estado que no conocemos SE MUESTRA, no se esconde ni se hace pasar por
 * disponible: si mañana la vista agrega uno, una herramienta pintada de verde
 * que en realidad está en otra cosa es peor que una etiqueta rara. */
export function estadoDe(fila) {
  const e = String(fila?.estado || "").toUpperCase();
  return ESTADOS[e] || { rotulo: e || "sin estado", clase: "hrr--raro", punto: "⚪" };
}

export const estaLibre = (fila) =>
  String(fila?.estado || "").toUpperCase() === "DISPONIBLE";

/* ── Cuántas hay, y si queda alguna ────────────────────────────────────────── */

/* SE PUEDE PRESTAR NO ES LO MISMO QUE ESTÁ DISPONIBLE, desde que una
 * herramienta tiene CANTIDAD.
 *
 * El estado describe a la herramienta entera y sirve para pintarla: con cien
 * guantes y uno sin devolver hace tres semanas, el tablero tiene que decir
 * DEMORADA —alguien tiene que ir a buscarlo— y al mismo tiempo dejar entregar
 * los noventa y nueve que están en el cajón. Con el estado solo, esos noventa
 * y nueve quedaban trabados por el que falta.
 *
 * Cuando la fila no trae `cantidad` —una vista vieja, un fixture— se cae a la
 * regla de antes. Suponer que hay unidades libres porque no vino el dato sería
 * inventar stock. */
export function hayLibres(fila) {
  const e = String(fila?.estado || "").toUpperCase();
  if (e === "MANTENIMIENTO" || fila?.fuera_de_servicio) return false;
  const total = Number(fila?.cantidad);
  if (Number.isFinite(total) && total > 0) {
    return (Number(fila?.disponibles) || 0) > 0;
  }
  return e === "DISPONIBLE";
}

/* «3 en uso · 97 libres».
 *
 * Vacío cuando hay una sola: «0 en uso · 1 libre» al lado de un cartel que ya
 * dice DISPONIBLE es la misma frase dos veces, y en un tablero de treinta
 * herramientas esa repetición es la que hace que no se lea ninguna. El renglón
 * aparece sólo donde agrega algo. */
export function unidades(fila) {
  const total = Number(fila?.cantidad);
  if (!Number.isFinite(total) || total <= 1) return "";
  const usadas = Number(fila?.prestadas) || 0;
  const libres = Number.isFinite(Number(fila?.disponibles))
    ? Number(fila.disponibles) : total - usadas;
  return `${usadas} en uso · ${libres} libre${libres === 1 ? "" : "s"} de ${total}`;
}

export const estaPrestada = (fila) =>
  ["PRESTADA", "DEMORADA"].includes(String(fila?.estado || "").toUpperCase());

/* ── La cuenta de arriba ──────────────────────────────────────────────────── */

export function resumen(filas) {
  const r = { disponibles: 0, prestadas: 0, demoradas: 0, mantenimiento: 0,
              total: 0 };
  for (const f of filas || []) {
    r.total += 1;
    const e = String(f.estado || "").toUpperCase();
    if (e === "DISPONIBLE") r.disponibles += 1;
    else if (e === "PRESTADA") r.prestadas += 1;
    else if (e === "DEMORADA") r.demoradas += 1;
    else if (e === "MANTENIMIENTO") r.mantenimiento += 1;
  }
  return r;
}

/* ── Buscar ───────────────────────────────────────────────────────────────── */

/* Sin tildes y en mayúscula, para comparar. Es la misma idea que
 * `consultas._pelado` del bot: quien escribe «amoladora» en el buscador no
 * tiene por qué acordarse de si el nombre lleva tilde.
 *
 * LA Ñ TAMBIÉN CAE, y es a propósito. «pena» encuentra a PEÑA. En un padrón
 * serían dos apellidos distintos y estaría mal; en un buscador de tres letras
 * lo que sobra se ve y se descarta de un vistazo, y lo que falta manda a
 * alguien a pensar que la herramienta no está cargada. */
export const pelar = (t) =>
  String(t ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .trim();

/* POR HERRAMIENTA Y POR PERSONA, en el mismo campo.
 *
 * Igna: *«debe poder buscarse tanto "Amoladora" como "Carlos Ruiz"»*. Dos
 * buscadores separados obligarían a saber de antemano qué se está buscando, y
 * el caso real es al revés: alguien mira el tablero, ve un hueco, y quiere
 * saber quién lo tiene — o busca a una persona para reclamarle todo junto.
 */
export function coincide(fila, texto) {
  const q = pelar(texto);
  if (!q) return true;
  return [fila.nombre, fila.codigo, fila.categoria, fila.sector,
          fila.persona_nombre]
    .some((v) => pelar(v).includes(q));
}

/* ── Filtrar ──────────────────────────────────────────────────────────────── */

export const FILTROS = [
  ["todas", "Todas"],
  ["DISPONIBLE", "Disponibles"],
  ["PRESTADA", "En uso"],
  ["DEMORADA", "Demoradas"],
  ["MANTENIMIENTO", "Fuera de servicio"],
];

/* «En uso» incluye las demoradas: una demorada sigue estando en la mano de
 * alguien. Al revés no —«Demoradas» son sólo ésas— porque ese filtro existe
 * justamente para aislarlas. */
export function filtrar(filas, { texto = "", estado = "todas", categoria = "",
                                 sector = "" } = {}) {
  return (filas || []).filter((f) => {
    if (!coincide(f, texto)) return false;
    if (categoria && f.categoria !== categoria) return false;
    if (sector && f.sector !== sector) return false;
    if (estado === "todas") return true;
    if (estado === "PRESTADA") return estaPrestada(f);
    return String(f.estado || "").toUpperCase() === estado;
  });
}

export const valoresDe = (filas, campo) =>
  [...new Set((filas || []).map((f) => f[campo]).filter(Boolean))].sort();

/* ── Cuándo ───────────────────────────────────────────────────────────────── */

const dosCifras = (n) => String(n).padStart(2, "0");

/* «14/09 13:42» — el día y la hora, que es lo que se lee de un vistazo. El año
 * no entra: una herramienta prestada hace más de un año es un problema de otra
 * naturaleza y para eso está el detalle. */
export function cuando(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${dosCifras(d.getDate())}/${dosCifras(d.getMonth() + 1)} ` +
         `${dosCifras(d.getHours())}:${dosCifras(d.getMinutes())}`;
}

export function fechaLarga(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${dosCifras(d.getDate())}/${dosCifras(d.getMonth() + 1)}/` +
         `${d.getFullYear()} ${dosCifras(d.getHours())}:${dosCifras(d.getMinutes())}`;
}

/* «hace 3 días» / «hace 5 h» / «recién».
 *
 * Se calcula desde `horas_prestada`, que lo manda la vista con el reloj DE LA
 * BASE. Restar contra el reloj del navegador daría un número distinto en cada
 * máquina mal puesta en hora, y «hace 2 días» es justamente el dato con el que
 * alguien decide ir a reclamar. */
export function haceCuanto(horas) {
  // `Number(null)` es 0, así que sin esta guarda «no sé hace cuánto» se leía
  // como «recién» — el peor de los dos, porque una herramienta que falta hace
  // una semana aparecería como recién retirada.
  if (horas === null || horas === undefined || horas === "") return "";
  const h = Number(horas);
  if (!Number.isFinite(h) || h < 0) return "";
  if (h < 1) return "recién";
  if (h < 24) return `hace ${Math.floor(h)} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "hace 1 día" : `hace ${d} días`;
}

/* Cuánto le queda antes de entrar en demora, para el detalle. Negativo = ya
 * está demorada, y se dice con esas palabras y no con un número en rojo. */
/* CUÁNTO TIEMPO LA TUVO. De `retirado_en` a `devuelto_en`.
 *
 * Es otra pregunta que «hace cuánto»: aquélla mira un préstamo ABIERTO contra
 * el reloj de ahora; ésta mide uno CERRADO, y el reloj no entra. Con una sola
 * función, un préstamo devuelto en marzo diría «hace 180 días» —que es cierto
 * y no es lo que se preguntó—.
 *
 * `""` cuando no se puede calcular: falta una fecha, no se entienden, o la
 * devolución es anterior al retiro. Un «0 h» inventado se lee como que la
 * devolvió en el acto, que es un dato, y acá no hay ninguno.
 */
export function duracion(desde, hasta) {
  const a = Date.parse(desde);
  const b = Date.parse(hasta);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return "";
  const ms = b - a;
  if (ms < 0) return "";
  const h = ms / 3600000;
  if (h < 1) return "menos de 1 h";
  if (h < 24) return `${Math.floor(h)} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? "1 día" : `${d} días`;
}

export function plazo(fila, ahora = new Date()) {
  if (!estaPrestada(fila)) return "";
  /* Con FECHA de devolución (Prestar, 07/10) manda la fecha, en días de
     Argentina: «vence mañana» se lee igual a las 8 que a las 23. */
  if (fila.vence_el && !fila.definitivo) {
    const faltan = diasEntre(hoyAr(ahora), fila.vence_el);
    if (faltan === null) return "";
    if (faltan < 0) {
      const d = -faltan;
      return `demorada hace ${d} día${d === 1 ? "" : "s"} (vencía el ${fechaCorta(fila.vence_el)})`;
    }
    return `vence ${textoVence(fila.vence_el, ahora)}`;
  }
  const dias = Number(fila.dias_prestamo) || 0;
  const pasadas = Number(fila.horas_prestada) || 0;
  const restantes = dias * 24 - pasadas;
  if (restantes <= 0) {
    const d = Math.floor(-restantes / 24);
    return d >= 1 ? `demorada hace ${d} día${d === 1 ? "" : "s"}`
                  : "demorada hace menos de un día";
  }
  if (restantes < 24) return `vence en ${Math.floor(restantes)} h`;
  const d = Math.floor(restantes / 24);
  return `vence en ${d} día${d === 1 ? "" : "s"}`;
}

/* ── ASIGNAR O PRESTAR (Nico, 06/10) ──────────────────────────────────────────
 *
 * *«Cuando da una herramienta, un toggle que por defecto está en Asignar (la
 * herramienta queda asignada a la persona, sin plazo de devolución). Al
 * pasarlo a Prestar aparece un campo para poner cuántos días la presta o la
 * fecha en que la tiene que devolver.»*
 *
 * La fecha es un DÍA (`taller_prestamo.vence_el`, columna `date`) y se cuenta
 * en hora de Argentina: prestar «por 3 días» un lunes a las 23:30 vence el
 * jueves, no el miércoles por la hora UTC del navegador.
 */

/* POR QUÉ SE CIERRA (07/10): volvió al pañol, se rompió o se perdió. Es lo
 * que permite saber cuánto dura lo que se asigna: sin esto, una llave rota y
 * una que volvió eran la misma fila (`taller_prestamo.cierre_motivo`). */
export const MOTIVOS_CIERRE = {
  devuelta: "Volvió al pañol",
  rota: "Se rompió",
  perdida: "Se perdió",
};

export const MODOS_ENTREGA = {
  asignar: { rotulo: "Asignar", ayuda: "Queda para la persona, sin fecha de devolución" },
  prestar: { rotulo: "Prestar", ayuda: "La tiene que devolver" },
};

export const DIAS_PRESTAMO_SUGERIDOS = 7;
const TOPE_DIAS = 365;

const TZ_AR = "America/Argentina/Buenos_Aires";

/* «2026-10-07» del día de hoy en Argentina. */
export function hoyAr(ahora = new Date()) {
  const d = ahora instanceof Date ? ahora : new Date(ahora);
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: TZ_AR, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(d);
  } catch {
    // Sin zonas horarias en el navegador: Argentina es UTC-3 todo el año.
    return new Date(d.getTime() - 3 * 3600000).toISOString().slice(0, 10);
  }
}

const diaUtc = (iso) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
};

export function sumarDias(iso, n) {
  const t = diaUtc(iso);
  if (!Number.isFinite(t)) return "";
  return new Date(t + Number(n) * 86400000).toISOString().slice(0, 10);
}

/* Días de `desde` a `hasta` (los dos «YYYY-MM-DD»). `null` si no se entienden. */
export function diasEntre(desde, hasta) {
  const a = diaUtc(desde);
  const b = diaUtc(hasta);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86400000);
}

const DIAS_SEMANA = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

/* «10/10» */
export function fechaCorta(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : "";
}

/* «hoy», «mañana», «el vie 10/10». */
export function textoVence(iso, ahora = new Date()) {
  const n = diasEntre(hoyAr(ahora), iso);
  if (n === null) return "";
  if (n === 0) return "hoy";
  if (n === 1) return "mañana";
  const dia = DIAS_SEMANA[new Date(diaUtc(iso)).getUTCDay()];
  return `el ${dia} ${fechaCorta(iso)}`;
}

/* El plazo de UN préstamo (una fila de `taller_prestamo`), para las listas:
   «la devuelve mañana», «demorada hace 2 días». Vacío si no tiene fecha. */
export function plazoDePrestamo(p, ahora = new Date()) {
  if (!p || p.definitivo || !p.vence_el || p.devuelto_en) return "";
  const n = diasEntre(hoyAr(ahora), p.vence_el);
  if (n === null) return "";
  if (n < 0) return `demorada hace ${-n} día${n === -1 ? "" : "s"}`;
  return `la devuelve ${textoVence(p.vence_el, ahora)}`;
}

/* Lo que eligió en el toggle, traducido a lo que se guarda:
 * `{ok, definitivo, vence, error}`. Asignar no tiene fecha. Prestar pide
 * días (1 a 365) o una fecha de hoy en adelante; si vienen las dos, manda la
 * fecha, que es lo último que se toca en la pantalla y lo que se lee. */
export function plazoElegido({ modo = "asignar", dias = "", fecha = "" } = {}, ahora = new Date()) {
  if (modo !== "prestar") return { ok: true, definitivo: true, vence: null, error: "" };
  const hoy = hoyAr(ahora);
  if (String(fecha || "").trim()) {
    const n = diasEntre(hoy, fecha);
    if (n === null) return { ok: false, definitivo: false, vence: null, error: "La fecha no se entiende." };
    if (n < 0) return { ok: false, definitivo: false, vence: null, error: "La fecha de devolución ya pasó." };
    if (n > TOPE_DIAS) return { ok: false, definitivo: false, vence: null, error: "Más de un año no es un préstamo: asignala." };
    return { ok: true, definitivo: false, vence: sumarDias(fecha, 0), error: "" };
  }
  const d = Number(dias);
  if (String(dias ?? "").trim() === "" || !Number.isInteger(d) || d < 1) {
    return { ok: false, definitivo: false, vence: null,
             error: "Poné cuántos días la presta o la fecha en que la tiene que devolver." };
  }
  if (d > TOPE_DIAS) return { ok: false, definitivo: false, vence: null, error: "Más de un año no es un préstamo: asignala." };
  return { ok: true, definitivo: false, vence: sumarDias(hoy, d), error: "" };
}

/* ── La grilla ────────────────────────────────────────────────────────────── */

/* El tablero es una grilla de 24 columnas. La posición se guarda en CELDAS y
 * no en píxeles para que el mismo tablero entre en el monitor del taller y en
 * el teléfono del pañolero. */
export const COLUMNAS = 24;

/* Cuántas filas de alto necesita el tablero para que entren todas. Se calcula
 * de lo que hay y no se fija en código: un tablero con una herramienta abajo
 * de todo tiene que dibujarla, no recortarla. */
export function altoDelTablero(filas, minimo = 12) {
  const max = (filas || []).reduce(
    (m, f) => Math.max(m, (Number(f.y) || 0) + (Number(f.alto) || 1)), 0);
  return Math.max(minimo, max);
}

/* Un lugar libre para una herramienta nueva: la primera fila entera vacía
 * abajo de todo. Es deliberadamente tonto —no busca huecos entre medio—
 * porque el que arma el tablero la va a arrastrar a mano igual, y adivinar un
 * hueco chico la deja donde nadie la buscaba. */
export function lugarLibre(filas, ancho = 3, alto = 4) {
  return { x: 0, y: altoDelTablero(filas, 0), ancho, alto };
}

/* ── DOS HERRAMIENTAS NO COMPARTEN UN LUGAR DE LA PARED ──────────────────────
 *
 * Nico, 20/10, con captura y «BUG» escrito a mano: la chapa del taladro
 * neumático tapaba el destornillador plano y salía cortada arriba. En la base
 * el taladro estaba en el 0,0 —y la agujereadora y el alicate también—, encima
 * del destornillador plano, que ocupa del 0,0 al 4,4. Las tres se cargaron el
 * 03/10, todas juntas, al pasar las asignaciones viejas (AS000000xx), sin
 * posición: quedaron en el 0,0 de 2×2 que pone la base. Lo mismo le pasa a
 * cualquier alta que no diga dónde va (la de Cepi por WhatsApp, por ejemplo,
 * no lo dice). Y el Hub las dibujaba ahí: cuatro en las mismas celdas, la
 * última arriba de todo y las otras dos escondidas abajo, sin que nada lo
 * dijera.
 *
 * La que estaba primero se queda; la que cae sobre un lugar ocupado SE MUESTRA
 * ABAJO DE TODO, una al lado de la otra, donde `lugarLibre` pone las nuevas.
 * Abajo se ve, se toca y se arrastra a su lugar con «Editar tablero». No se
 * escribe nada: lo guardado sigue igual hasta que alguien la acomode.
 *
 * «PRIMERO» NO ES LA MÁS DE ARRIBA A LA IZQUIERDA. Con ese orden, una recién
 * llegada al 0,0 le ganaba el lugar a una que empezaba en la columna 1, y la
 * que sí tenía su lugar se iba al fondo. Entonces:
 *   1. ceden las que están en el lugar que pone la base (0,0 de 2×2): casi
 *      seguro nadie las puso ahí, y una que alguien acomodó a mano en la
 *      esquina tiene que ganarle aunque sea más nueva;
 *   2. entre las demás, la más vieja (id más chico) se queda.
 * Así siempre gana la misma, venga en el orden que venga.
 *
 * Recibe `[{id, x, y, ancho, alto}]` y devuelve sólo las que hubo que correr:
 * `{[id]: {x, y, ancho, alto}}`. Vacío es que no se encima ninguna. */
// El lugar que pone la base cuando el alta no dice dónde va (ver la migración
// del tablero: x e y en 0, ancho y alto en 2).
const enElLugarDeLaBase = (l) =>
  l.x === 0 && l.y === 0 && l.ancho === 2 && l.alto === 2 ? 1 : 0;

const seEnciman = (a, b) =>
  a.x < b.x + b.ancho && b.x < a.x + a.ancho &&
  a.y < b.y + b.alto && b.y < a.y + a.alto;

export function sinEncimar(lugares) {
  const lista = (lugares || []).map((l) => ({
    id: l.id,
    x: Number(l.x) || 0,
    y: Number(l.y) || 0,
    ancho: Math.min(Math.max(1, Number(l.ancho) || 1), COLUMNAS),
    alto: Math.max(1, Number(l.alto) || 1),
  })).sort((a, b) => enElLugarDeLaBase(a) - enElLugarDeLaBase(b) ||
                     Number(a.id) - Number(b.id) || a.y - b.y || a.x - b.x);

  const quedan = [];
  const corridas = [];
  for (const l of lista) {
    (quedan.some((q) => seEnciman(q, l)) ? corridas : quedan).push(l);
  }

  const movidas = {};
  let x = 0;
  let y = altoDelTablero(quedan, 0);
  let altoFila = 0;
  for (const l of corridas) {
    if (x + l.ancho > COLUMNAS) { y += altoFila; x = 0; altoFila = 0; }
    movidas[l.id] = { x, y, ancho: l.ancho, alto: l.alto };
    x += l.ancho;
    altoFila = Math.max(altoFila, l.alto);
  }
  return movidas;
}

/* ── El código interno ────────────────────────────────────────────────────── */

/* EL CÓDIGO SE PROPONE. La base lo pide —es único y no puede ser nulo— y la
 * pantalla lo pedía en un campo vacío, sin decir de dónde sacarlo. El que
 * está parado frente a la pared no tiene ningún código: tiene un martillo.
 *
 * Se vio pasar: la única herramienta que alguien llegó a cargar a mano quedó
 * con código `0899402150` —el código de barras del envoltorio—, porque era el
 * único número a mano. Un campo obligatorio que nadie sabe llenar no se llena
 * mal: frena la carga.
 *
 * Se propone y NO se genera a escondidas: el código se imprime en la etiqueta
 * que va pegada a la herramienta, y el taller tiene que poder imponer el suyo.
 * Llega escrito y se puede pisar.
 */

/* El prefijo sale del tipo —lo que la herramienta ES— y reproduce el del
 * catálogo con el que se sembró el tablero: una palabra da sus cuatro primeras
 * letras (MARTillo → MART), varias dan dos de cada una, hasta cinco
 * (MArtillo BOla → MABO, PInza PIco LOro → PIPIL). */
export function prefijoDeCodigo(tipo, nombre = "") {
  const base = pelar(tipo || "").replace(/[^A-Z0-9_ ]/g, "").trim()
    || pelar(nombre || "").replace(/[^A-Z0-9 ]/g, "").trim();
  const palabras = base.split(/[_\s]+/).filter(Boolean);
  if (!palabras.length) return "HERR";
  if (palabras.length === 1) return palabras[0].slice(0, 4);
  return palabras.map((p) => p.slice(0, 2)).join("").slice(0, 5);
}

/* El primer correlativo LIBRE, mirando todos los códigos que ya existen —los
 * de las herramientas dadas de baja también—. El único índice de la tabla es
 * único sobre `codigo` y no le importa si la herramienta está activa: proponer
 * un código que ya usó una que se dio de baja termina en un error que el que
 * carga no puede explicarse. */
export function codigoSugerido(tipo, nombre, filas) {
  const pre = prefijoDeCodigo(tipo, nombre);
  const usados = new Set((filas || []).map((f) => pelar(f?.codigo)));
  for (let n = 1; n <= 999; n += 1) {
    const cod = `${pre}-${String(n).padStart(2, "0")}`;
    if (!usados.has(cod)) return cod;
  }
  return "";
}

/* ── Buscar a una persona en el tablero ───────────────────────────────────────
 *
 * Nico, 01/10: *«cuando se está buscando a alguien que se empiece a rellenar
 * […]; que la búsqueda sea exacta —si pongo Insaurralde y hay más de uno, que
 * me deje elegir a quién—; y lo que tiene asignado, que se ponga primero»*.
 *
 * El buscador del tablero comparaba el texto contra `persona_nombre` de cada
 * fila. Dos cosas lo hacían inexacto: «Insaurralde» prendía lo de TODOS los
 * Insaurralde juntos, y la vista trae sólo el préstamo MÁS VIEJO de cada
 * herramienta —así que los guantes que se le dieron a uno, si antes alguien
 * más se llevó otro par, ni siquiera se prendían—. Elegida la persona, lo que
 * tiene sale de SUS préstamos (por DNI), no del nombre pegado a la pared.
 */

/* Las herramientas que el texto nombra, para la lista que se despliega. Por
 * nombre y código: lo que alguien escribe cuando busca una herramienta. */
export function herramientasQueCoinciden(filas, texto, tope = 6) {
  const q = pelar(texto);
  if (q.length < 2) return [];
  return (filas || [])
    .filter((f) => [f.nombre, f.codigo].some((v) => pelar(v).includes(q)))
    .slice(0, tope);
}

/* Lo que tiene una persona, en el orden en que se lo mira: PRIMERO lo que se
 * le asignó —es suyo, es lo que se le va a ver encima— y después lo prestado,
 * lo más viejo arriba, que es lo primero que hay que reclamar. */
export function ordenLoQueTiene(prestamos) {
  const abiertos = (prestamos || []).filter((p) => !p.devuelto_en && !p.anulado_en);
  const viejoPrimero = (a, b) =>
    String(a.retirado_en || "").localeCompare(String(b.retirado_en || ""));
  return {
    asignadas: abiertos.filter((p) => p.definitivo).sort(viejoPrimero),
    prestadas: abiertos.filter((p) => !p.definitivo).sort(viejoPrimero),
  };
}

/* Qué herramientas del tablero se prenden: las de SUS préstamos abiertos. */
export const idsDeLoQueTiene = (prestamos) =>
  new Set((prestamos || [])
    .filter((p) => !p.devuelto_en && !p.anulado_en)
    .map((p) => p.herramienta_id));

/* ── LAS CAJAS DE HERRAMIENTAS ────────────────────────────────────────────────
 *
 * Nico, 02/10: *«el 90% es una caja de herramientas asignada»*. El tablero
 * compartido —sacar, prestar, devolver— es el 10%. Lo de todos los días es
 * que cada mecánico o chofer tiene SU caja con SUS herramientas, que no se
 * devuelven ni se demoran.
 *
 * La base ya lo distinguía: `taller_prestamo.definitivo = true` es lo
 * asignado (#272, «se la doy»). Una caja no es una tabla nueva: es el conjunto
 * de entregas definitivas abiertas de una persona. Así lo que se asigna por
 * WhatsApp («asigné / se la queda») cae en la misma caja que lo del Hub.
 */

const clavePersona = (p) => String(p.persona_dni || p.persona_nombre || "").trim();

/* Una caja por persona con algo asignado: `[{dni, nombre, herramientas,
   unidades, desde, sinFoto, destinatario}]`, por nombre. `herramientas` cuenta
   tipos distintos y `unidades` cada entrega: tres llaves 10 son una
   herramienta y tres unidades.

   P-311: las ENTREGAS del pañol (`panol_entregas`, el papel) también arman
   cajas. Lo escrito a mano en una entrega —las actas viejas de elementos, lo
   que no está en el catálogo— suma a la caja de esa persona, y cada entrega
   sin la foto del papel firmado suma a `sinFoto`. Lo del catálogo de una
   entrega NO suma de nuevo: ya está por su préstamo. Las entregas sin DNI no
   arman caja (no se le adjudican a nadie por parecido): las lista aparte la
   pantalla. */
export function agruparCajas(prestamos, entregas = []) {
  const porPersona = new Map();
  const cajaDe = (k, dni, nombre) => {
    const c = porPersona.get(k) || {
      dni: dni || null, nombre: nombre || dni,
      ids: new Set(), unidades: 0, desde: null, sinFoto: 0,
      destinatario: null, ultima: "",
    };
    porPersona.set(k, c);
    return c;
  };
  for (const p of prestamos || []) {
    if (!p || !p.definitivo || p.devuelto_en || p.anulado_en) continue;
    const k = clavePersona(p);
    if (!k) continue;
    const c = cajaDe(k, p.persona_dni, p.persona_nombre);
    c.ids.add(p.herramienta_id);
    c.unidades += 1;
    if (p.retirado_en && (!c.desde || p.retirado_en < c.desde)) c.desde = p.retirado_en;
  }
  for (const e of entregas || []) {
    if (!e || e.anulado_en || !String(e.persona_dni || "").trim()) continue;
    const k = String(e.persona_dni).trim();
    const sueltos = (Array.isArray(e.items) ? e.items : [])
      .filter((it) => it && !it.herramienta_id && String(it.nombre || "").trim());
    if (!porPersona.has(k) && !sueltos.length) continue;
    const c = cajaDe(k, e.persona_dni, e.persona_nombre);
    for (const it of sueltos) {
      c.ids.add(`a mano:${pelar(it.nombre)}`);
      c.unidades += Number(it.cantidad) || 1;
    }
    const dia = e.entregado_en || "";
    if (dia && (!c.desde || dia < c.desde)) c.desde = dia;
    if (!e.foto_path) c.sinFoto += 1;
    // El tipo de la entrega más nueva: si alguien pasó de chofer a mecánico,
    // lo último que se le dio dice qué es hoy.
    const cuando = String(e.creado_en || e.entregado_en || "");
    if (e.destinatario && cuando >= c.ultima) {
      c.destinatario = e.destinatario;
      c.ultima = cuando;
    }
  }
  return [...porPersona.values()]
    .map(({ ids, ultima, ...c }) => ({ ...c, herramientas: ids.size }))
    .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), "es"));
}

/* La observación de una entrega, sin la marca «entrega con papel #N» que pone
   la base: es lo que escribió quien la entregó. */
export function notaDeEntrega(nota) {
  return String(nota || "").replace(/^entrega con papel #\d+\s*(·\s*)?/i, "").trim();
}

/* Lo que hay adentro de una caja, una ficha por herramienta con cuántas
   unidades tiene: `[{herramienta_id, nombre, codigo, tipo, cantidad,
   prestamos, desde, notas}]`. `notas`: las observaciones de sus entregas,
   sin repetir. `prestamos` son los ids de las entregas, del más nuevo
   al más viejo: sacar una de la caja cierra la más nueva. */
export function contenidoDeCaja(prestamos) {
  const porHerramienta = new Map();
  for (const p of prestamos || []) {
    if (!p || p.devuelto_en || p.anulado_en) continue;
    const k = p.herramienta_id;
    const f = porHerramienta.get(k) || {
      herramienta_id: k,
      nombre: p.herramienta?.nombre || p.herramienta?.codigo || "Herramienta",
      codigo: p.herramienta?.codigo || "",
      tipo: p.herramienta?.tipo || "generica",
      cantidad: 0, entregas: [], desde: null, notas: [],
    };
    f.cantidad += 1;
    const nota = notaDeEntrega(p.nota);
    if (nota && !f.notas.includes(nota)) f.notas.push(nota);
    f.entregas.push(p);
    if (p.retirado_en && (!f.desde || p.retirado_en < f.desde)) f.desde = p.retirado_en;
    porHerramienta.set(k, f);
  }
  return [...porHerramienta.values()]
    .map(({ entregas, ...f }) => ({
      ...f,
      prestamos: entregas
        .sort((a, b) => String(b.retirado_en || "").localeCompare(String(a.retirado_en || "")))
        .map((e) => e.id),
    }))
    .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), "es"));
}

/* Lo que el pañol tiene afuera: las entregas que NO son de una caja. Una está
   demorada cuando pasó su `dias_prestamo` — la misma regla que la vista
   `taller_tablero`, contada por entrega y no por herramienta: cien guantes con
   tres afuera son tres préstamos, aunque la herramienta figure DISPONIBLE. */
export function cuentaPanol(prestamos, ahora = new Date()) {
  let prestadas = 0;
  let demoradas = 0;
  for (const p of prestamos || []) {
    if (!p || p.definitivo || p.devuelto_en || p.anulado_en) continue;
    prestadas += 1;
    // Con fecha de devolución (07/10), vencida es ya estar en el día siguiente.
    if (p.vence_el) {
      if (hoyAr(ahora) > String(p.vence_el).slice(0, 10)) demoradas += 1;
      continue;
    }
    const dias = Number(p.herramienta?.dias_prestamo) || 7;
    const vence = new Date(p.retirado_en).getTime() + dias * 86400000;
    if (Number.isFinite(vence) && ahora.getTime() > vence) demoradas += 1;
  }
  return { prestadas, demoradas };
}

/* La búsqueda de una persona, como filtro de PostgREST.
 *
 * Igna, 02/10: buscó «celis, carlos» y la pantalla contestó «failed to parse
 * logic tree». El texto iba crudo adentro de `or=(…)`, donde la COMA separa
 * condiciones: la coma de «apellido, nombre» —que es como el padrón escribe a
 * la gente, y como la escribe cualquiera— rompía el filtro entero.
 *
 * Ahora el texto se parte en palabras, sin signos y sin tildes (el padrón va
 * en mayúsculas sin acentos: 1 de 356 tiene tilde), y CADA palabra tiene que
 * aparecer en el apellido, el nombre o el DNI. «celis carlos», «carlos celis»
 * y «CELIS, CARLOS» encuentran a los mismos dos. Devuelve "" si no queda nada
 * para buscar. */
export function filtroDePersonas(texto, topePalabras = 4) {
  // La ñ NO es una n con tilde: «IBAÑEZ» no se encuentra buscando «Ibanez».
  const sinTildes = (c) => (/[ñÑ]/.test(c) ? c
    : c.normalize("NFD").replace(/[̀-ͯ]/g, ""));
  const palabras = Array.from(String(texto ?? "")).map(sinTildes).join("")
    .replace(/[^0-9A-Za-zÑñ]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, topePalabras);
  if (!palabras.length) return "";
  const una = (w) => `or(apellido.ilike.%${w}%,nombres.ilike.%${w}%,dni.ilike.%${w}%)`;
  return `and(${palabras.map(una).join(",")})`;
}

/* ── CÓMO SE VE CADA CAJA EN EL ESTANTE ───────────────────────────────────────
 *
 * Igna, 02/10: el estante de cajas como una estantería de metal con cajas de
 * herramientas de colores, y cada caja con el nombre de la persona.
 *
 * EL COLOR ES EL DE LA EMPRESA de la persona: TA verde, TC azul, EX roja,
 * TCC púrpura —los del chip de empresa del resto del Hub, que fijó Nico el
 * 08/09 (Igna, 02/10: «EX es ROJO y TA es VERDE»). Sale de `personas.empresa`, que guarda el CUIT, y se
 * traduce con `siglaDe` —el mismo glosario que usa el resto del Hub—. Sin
 * empresa cargada, o con una que no es de estas cuatro, la caja es
 * GALVANIZADA: sin pintar, que es justo lo que dice el dato. */
export const COLORES_POR_EMPRESA = { TA: "verde", TC: "azul", EX: "roja", TCC: "purpura" };
export const COLOR_SIN_EMPRESA = "galvanizada";
export const COLORES_CAJA = [...Object.values(COLORES_POR_EMPRESA), COLOR_SIN_EMPRESA];
// Un solo modelo desde que las cajas son la foto del baúl (ver CajaDibujo):
// queda la lista para cuando haya foto de la de bandejas.
export const MODELOS_CAJA = ["baul"];

export function aspectoDeCaja(empresa) {
  const sigla = siglaDe(empresa);
  return {
    color: COLORES_POR_EMPRESA[sigla] || COLOR_SIN_EMPRESA,
    modelo: MODELOS_CAJA[0],
    sigla: COLORES_POR_EMPRESA[sigla] ? sigla : null,
  };
}

/* «RUIZ, CARLOS ALBERTO» → apellido «RUIZ», nombres «CARLOS ALBERTO». Sin coma,
   la primera palabra es el apellido (así escribe el padrón cuando no la pone). */
export function partesDelNombre(nombre) {
  const t = String(nombre ?? "").trim();
  if (!t) return { apellido: "", nombres: "" };
  if (t.includes(",")) {
    const [a, ...r] = t.split(",");
    return { apellido: a.trim(), nombres: r.join(",").trim() };
  }
  const [a, ...r] = t.split(/\s+/);
  return { apellido: a, nombres: r.join(" ") };
}
